import { useState, type FormEvent } from 'react';

const SOCIAL_LINKS = [
  { icon: 'ri-github-fill', href: 'https://github.com/guilherme-marostica-dev', label: 'GitHub', handle: '@Guilherme-marostica-dev' },
  { icon: 'ri-linkedin-fill', href: 'https://www.linkedin.com/in/guilhermesmarostica/', label: 'LinkedIn', handle: 'in/guilhermesmarostica' },
  { icon: 'ri-code-s-slash-line', href: 'https://dev.to/gmarostica_dev', label: 'Dev.to', handle: '@gmarostica_dev' },
];

export default function ContactSection() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = form.querySelector('textarea')?.value || '';
    if (message.length > 500) {
      alert('Message must be 500 characters or less.');
      return;
    }
    setStatus('sending');
    try {
      const data = new URLSearchParams();
      const formData = new FormData(form);
      formData.forEach((value, key) => {
        data.append(key, value as string);
      });
      const res = await fetch('https://readdy.ai/api/form/d830d26k6npfa23qg8n0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="py-24 bg-[#FAFAF8]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left */}
          <div>
            <span className="inline-block px-4 py-1.5 bg-emerald-50 text-emerald-700 text-xs font-semibold tracking-widest uppercase rounded-full mb-6">
              Get In Touch
            </span>
            <h2
              className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-5"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Let's start a
              <br />
              conversation
            </h2>
            <p className="text-gray-500 leading-relaxed mb-10 text-sm md:text-base">
              Have a project in mind, a role to fill, or just want to say hi?
              I'd love to hear from you. I typically respond within 24 hours.
            </p>

            {/* Social Links */}
            <div className="flex flex-col gap-4">
              {SOCIAL_LINKS.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="flex items-center gap-4 group cursor-pointer"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gray-100 text-gray-700 group-hover:bg-emerald-500 group-hover:text-white transition-all">
                    <i className={`${s.icon} text-lg`}></i>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-900">{s.label}</div>
                    <div className="text-xs text-gray-400">{s.handle}</div>
                  </div>
                  <i className="ri-external-link-line text-gray-300 group-hover:text-emerald-500 ml-auto transition-colors"></i>
                </a>
              ))}
            </div>
          </div>

          {/* Right — Form */}
          <div>
            {status === 'success' ? (
              <div className="h-full flex items-center justify-center rounded-2xl bg-emerald-50 border border-emerald-100 p-10 text-center">
                <div>
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-emerald-500 text-white mx-auto mb-4">
                    <i className="ri-check-line text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Message sent!</h3>
                  <p className="text-gray-500 text-sm">Thanks for reaching out — I'll get back to you soon.</p>
                  <button
                    onClick={() => setStatus('idle')}
                    className="mt-6 px-5 py-2 bg-emerald-500 text-white text-sm font-medium rounded-full hover:bg-emerald-600 transition-colors cursor-pointer"
                  >
                    Send Another
                  </button>
                </div>
              </div>
            ) : (
              <form
                data-readdy-form
                id="portfolio-contact-form"
                onSubmit={handleSubmit}
                className="bg-white rounded-2xl border border-gray-100 p-8 flex flex-col gap-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="John Doe"
                      className="px-4 py-3 bg-gray-50 border border-gray-100 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent transition"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="john@example.com"
                      className="px-4 py-3 bg-gray-50 border border-gray-100 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent transition"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                    Subject
                  </label>
                  <select
                    name="subject"
                    className="px-4 py-3 bg-gray-50 border border-gray-100 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent transition cursor-pointer"
                  >
                    <option value="Job Opportunity">Job Opportunity</option>
                    <option value="Freelance Project">Freelance Project</option>
                    <option value="Collaboration">Collaboration</option>
                    <option value="General Inquiry">General Inquiry</option>
                  </select>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                    Message * <span className="text-gray-400 normal-case font-normal">(max 500 chars)</span>
                  </label>
                  <textarea
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="Tell me about your project or opportunity..."
                    className="px-4 py-3 bg-gray-50 border border-gray-100 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent transition resize-none"
                  />
                </div>

                {status === 'error' && (
                  <p className="text-red-500 text-xs">Something went wrong. Please try again.</p>
                )}

                <button
                  type="submit"
                  disabled={status === 'sending'}
                  className="flex items-center justify-center gap-2 py-3.5 bg-gray-900 text-white font-semibold rounded-lg hover:bg-emerald-600 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status === 'sending' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <i className="ri-send-plane-line"></i>
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}